import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getUserSelector } from '../../../user/selectors/getUser.selector'
import { getSessionResumeSelector } from '../store/sessionResume.selectors'
import { AppRouterConstants } from '../../../../components/appRouter/AppRouter.constants'
import { setSessionResumeShowModal } from '../store/sessionResume.actions'
import sessionResumeService from '../service/sessionResume.service'
import { SessionPinModal } from '../../components/SessionPinModal'
import { verifyUserPin } from '../../../login/util/login.util'
import { getDisplayUser } from '../utils/sessionResume.util'
import { sessionPersistService } from '../../persist/service/sessionPersist.service'
import { handleApiError } from '../../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { ZollAPIService } from '../../../../services/zollApi.service'
import { isTokenValid } from '../../../../utilities/auth.util'
import { isOffline } from '../../../../utilities/isOnline.util'
import { AuthenticationService } from '../../../login/services/authentication.service'

const RESUME_DELAY_MS = 500

const SessionResumeModal = () => {
  const dispatch = useDispatch()
  const user = useSelector(getUserSelector)
  const session = useSelector(getSessionResumeSelector)

  const { userId, serviceId } = user
  const { showSessionResumeModal, user: sessionUser } = session

  const [pinValues, setPinValues] = useState<string[]>([])
  const [isResuming, setIsResuming] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const clearPinAndError = () => {
    setPinValues([])
    setErrorMessage('')
  }

  const handleLogout = () => {
    window.location.href = AppRouterConstants.LOGIN_ROUTE
  }

  const handlePinChange = (numbers: string[]) => {
    setTimeout(() => {
      setPinValues(numbers)
      setErrorMessage('')
    }, 0)
  }

  const handleSessionExpired = () => {
    sessionResumeService.clearScheduledTimeout()
    dispatch(handleApiError(ZollAPIService.createInvalidAuthenticationResponse()))
    setErrorMessage('Session expired. Please log in again.')
    setIsResuming(false)
    setPinValues([])
  }

  const handleResume = async () => {
    const pin = pinValues.join('')
    const result = await verifyUserPin(pin, userId, serviceId)

    if (result.success) {
      setIsResuming(true)

      if (isOffline()) {
        const authState = sessionUser?.authState || user.authState
        const preferredUsername =
          authState?.accessToken?.claims?.preferred_username?.toLowerCase() ||
          authState?.idToken?.claims?.preferred_username?.toLowerCase()
        const offlineAuth = AuthenticationService.getOfflineAuthState(preferredUsername || user.userName)
        if (!offlineAuth) {
          handleSessionExpired()
          return
        }
      } else {
        const authState = sessionUser?.authState || user.authState
        const tokenString = authState?.idToken?.idToken || authState?.accessToken?.accessToken
        if (!tokenString || !isTokenValid(tokenString)) {
          handleSessionExpired()
          return
        }
      }

      sessionResumeService.startSession()

      setTimeout(() => {
        setIsResuming(false)
        dispatch(setSessionResumeShowModal(false))
        clearPinAndError()
      }, RESUME_DELAY_MS)
    } else {
      setErrorMessage(result.error || 'Incorrect PIN, please try again')
    }
  }

  const handleStartNewSession = async () => {
    await sessionPersistService.clearMetadata()
    handleLogout()
  }

  return (
    <SessionPinModal
      isOpen={showSessionResumeModal}
      displayUser={getDisplayUser(sessionUser)}
      pinValues={pinValues}
      errorMessage={errorMessage}
      isResuming={isResuming}
      onPinChange={handlePinChange}
      onResume={handleResume}
      onStartNewSession={handleStartNewSession}
    />
  )
}

export default SessionResumeModal
